import type { AuthUser, Settings, SettingsStore } from '@mendikot/app-core';
import { createTranslator, type Translate } from '@mendikot/i18n';
import type { RemoteSettings } from '@mendikot/protocol';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import { create } from 'zustand';
import type { WebServices } from '../platform/services';

interface AppContextValue {
  services: WebServices;
  settings: SettingsStore;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ value, children }: { value: AppContextValue; children: ReactNode }) {
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('AppProvider missing');
  return ctx;
}

export const useServices = (): WebServices => useApp().services;

export function useSettings<T>(selector: (s: Settings) => T): T {
  return useStore(useApp().settings, selector);
}

export function useSettingsStore(): SettingsStore {
  return useApp().settings;
}

export function useT(): Translate {
  const lang = useSettings((s) => s.lang);
  return useMemo(() => createTranslator(lang), [lang]);
}

export function useAuthUser(): AuthUser | null {
  const { auth } = useServices();
  const [user, setUser] = useState<AuthUser | null>(() => auth.current());
  useEffect(() => auth.onChange(setUser), [auth]);
  return user;
}

export function useRemoteSettings(): RemoteSettings {
  const { remoteConfig } = useServices();
  const [settings, setSettings] = useState(() => remoteConfig.current());
  useEffect(() => remoteConfig.onChange(setSettings), [remoteConfig]);
  return settings;
}

/** Cross-screen UI state that is not persisted. */
interface UiState {
  /** True while a hand is being played — festival themes only switch at safe points. */
  inHand: boolean;
  setInHand(inHand: boolean): void;
}

export const useUi = create<UiState>((set) => ({
  inHand: false,
  setInHand: (inHand) => set({ inHand }),
}));
